import React, { ChangeEvent, useMemo, useState } from "react";
import { Control, Controller } from "react-hook-form";
import { CSS } from "@dnd-kit/utilities";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  useSortable,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { GripVertical, Plus, Trash } from "lucide-react";
import { NoxusInput } from "./InputField";
import { SpotSelect } from "./SelectField";
import type { OptionType } from "./SelectField";
import { NoxusFormLabel } from "./NoxusFormLabel";
import { NoxusIconButton } from "../UI/NoxusIconButton";
import { Button } from "../shadcn/button";
import { findMaxStringValue } from "@/utils/generic";
import { cn } from "@/lib/utils";
import {
  type AdvancedChipOption,
  chipOptionsToSuggestionItems,
} from "./TiptapEditor/chipOptionsConfig";

interface IInputVariableValue {
  label: string;
  /** Backend-readable type label (e.g. "Text", "List of Files"). Inferred
   * from the selected variable's data type. */
  value: string;
  /** Chip id of the bound upstream variable. Empty when nothing's picked. */
  variable?: string;
}

/**
 * Map a chip's (dataType, isList) to one of the type labels the backend
 * `_input_type_map` understands. Falls back to "Text" for anything we can't
 * map (object/dict/etc.) so the row is always serializable.
 *
 * Mirrors `spotflow/spotflow/nodes/codeexecution.py::_input_type_map`.
 */
function inferTypeLabel(
  dataType: string | undefined,
  isList: boolean | undefined
): string {
  const dt = (dataType ?? "").toLowerCase();
  if (dt === "file") return isList ? "List of Files" : "File";
  if (dt === "image") return isList ? "List of Images" : "Image";
  if (dt === "audio") return isList ? "List of Audios" : "Audio";
  return isList ? "List of Text" : "Text";
}

function SortableItem({
  id,
  children,
  isDisabled,
}: {
  id: string;
  children: React.ReactNode;
  isDisabled?: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id, disabled: isDisabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} className="w-full bg-white py-1">
      <div className="flex items-end">
        <div className="flex-1">{children}</div>
        <div
          {...attributes}
          {...listeners}
          className={cn(
            "p-3 -mb-0.5 cursor-grab text-primary",
            isDisabled && "cursor-not-allowed opacity-50"
          )}
        >
          <GripVertical size="1rem" fill="060C11" strokeWidth="1.25px" />
        </div>
      </div>
    </div>
  );
}

interface IInputVariableSelectorInputProps {
  isDisabled?: boolean;
  label: string;
  tooltip?: string;
  helperText?: string;
  value: IInputVariableValue[];
  onChange: (value: IInputVariableValue[]) => boolean;
  name?: string;
  name_label: string;
  name_prefix: string;
  value_label: string;
  default_value: string;
  min_items?: number;
  addLabel?: string;
  /**
   * Upstream variable pool — same shape that rich-text mentions consume.
   * The picker flattens these into combobox options and stores the chosen
   * variable id in the row's `value`.
   */
  chipOptions: AdvancedChipOption[];
}

const InputVariableSelectorInput = ({
  isDisabled,
  label,
  tooltip,
  helperText,
  value,
  onChange,
  name,
  name_label,
  name_prefix,
  value_label,
  default_value,
  min_items = 1,
  addLabel = "Add entry",
  chipOptions,
}: IInputVariableSelectorInputProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  const [inputs, setInputs] = useState<string[]>(
    value?.map((v) => v.label) || []
  );

  const variableOptions = useMemo<OptionType[]>(() => {
    return chipOptionsToSuggestionItems(chipOptions).map((item) => ({
      value: item.id,
      label: item.label,
      description: item.sourceName || item.group,
      icon: item.sourceImage,
      custom: { dataType: item.dataType, isList: item.isList },
    }));
  }, [chipOptions]);

  const addEntry = () => {
    const maxOutputValue = findMaxStringValue(
      value.map((i) => i.label),
      new RegExp(`^${name_prefix}(\\d+)$`)
    );
    const newName = `${name_prefix}${maxOutputValue + 1}`;
    if (onChange([...value, { label: newName, value: default_value }])) {
      setInputs([...inputs, newName]);
    } else {
      setInputs([...inputs]);
    }
  };

  const handleDeleteClick = (index: number) => {
    const newInputs = [...inputs];
    newInputs.splice(index, 1);
    setInputs(newInputs);
    if (onChange(value.filter((_, i) => i !== index))) {
      setInputs(newInputs);
    } else {
      setInputs([...inputs]);
    }
  };

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over) return;
    if (active.id === over.id) return;

    const new_value = arrayMove(
      value,
      value.findIndex((v) => v.label === active.id),
      value.findIndex((v) => v.label === over.id)
    );
    const new_inputs = arrayMove(
      inputs,
      inputs.findIndex((v) => v === active.id),
      inputs.findIndex((v) => v === over.id)
    );
    onChange(new_value);
    setInputs(new_inputs);
  }

  const onInputChange = (
    newValue: ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const newInputs = [...inputs];
    newInputs[index] = newValue.currentTarget.value;
    setInputs(newInputs);
  };

  const updateValueOnBlur = (_: React.FocusEvent, index: number) => {
    const oldInputs = structuredClone(inputs);
    const changeSuccess = onChange(
      value.map((v, i) => {
        if (i !== index) return v;
        const _value = inputs[i].trim() || v.label;
        setInputs((prev) =>
          prev.map((input, j) => (j === index ? _value : input))
        );
        return { ...v, label: _value };
      })
    );
    if (!changeSuccess) setInputs(oldInputs);
  };

  return (
    <div>
      <NoxusFormLabel label={label} tooltip={tooltip} name={name} />
      {helperText && (
        <p className="mt-1 text-xs text-muted-foreground">{helperText}</p>
      )}
      <div className="w-full mt-4 flex flex-col gap-3">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext
            items={value.map((v) => v.label)}
            strategy={verticalListSortingStrategy}
          >
            {value.map(({ label: rowLabel, variable }, index) => {
              const selected = variableOptions.find(
                (o) => o.value === variable
              );
              return (
                <SortableItem
                  key={rowLabel}
                  id={rowLabel}
                  isDisabled={isDisabled}
                >
                  <div className="w-full flex gap-4 items-center">
                    <div className="flex-1 w-1/2">
                      <NoxusInput
                        value={inputs[index]}
                        onChange={(e) => onInputChange(e, index)}
                        onBlur={(e) => updateValueOnBlur(e, index)}
                        label={name_label}
                        disable={isDisabled}
                      />
                    </div>
                    <div className="flex-1 w-1/2">
                      <SpotSelect
                        options={variableOptions}
                        value={selected}
                        onChange={(option) => {
                          const meta = (option?.custom ?? {}) as {
                            dataType?: string;
                            isList?: boolean;
                          };
                          const newValue = option
                            ? inferTypeLabel(meta.dataType, meta.isList)
                            : default_value;
                          onChange(
                            value.map((v, i) =>
                              i === index
                                ? {
                                    ...v,
                                    value: newValue,
                                    variable: option?.value,
                                  }
                                : v
                            )
                          );
                        }}
                        label={value_label}
                        placeholder="Select variable"
                        isDisabled={isDisabled}
                      />
                    </div>
                    <div className="self-end mb-1">
                      <NoxusIconButton
                        aria-label="Delete"
                        icon={Trash}
                        variant="smallSecondary"
                        onClick={() => handleDeleteClick(index)}
                        isDisabled={isDisabled || value.length <= min_items}
                        disabledTooltip={
                          value.length <= min_items
                            ? min_items === 1
                              ? "Can't have less than 1 value"
                              : `Can't have less than ${min_items} value${min_items === 1 ? "" : "s"}`
                            : undefined
                        }
                      />
                    </div>
                  </div>
                </SortableItem>
              );
            })}
          </SortableContext>
        </DndContext>
      </div>
      <Button
        onClick={addEntry}
        className="mt-2 w-full"
        variant="outline"
        isDisabled={isDisabled}
      >
        {addLabel}
        <Plus size="1.25rem" strokeWidth="1.25px" />
      </Button>
    </div>
  );
};

interface IInputVariableSelectorFieldProps {
  name: string;
  label: string;
  tooltip?: string;
  helperText?: string;
  defaultValue?: IInputVariableValue[];
  control: Control<any>;
  onChange?: (value: IInputVariableValue[]) => void;
  isDisabled?: boolean;
  name_label: string;
  name_prefix: string;
  value_label: string;
  default_value: string;
  min_items?: number;
  addLabel?: string;
  chipOptions: AdvancedChipOption[];
}

export const InputVariableSelectorField = ({
  name,
  label,
  tooltip,
  helperText,
  control,
  defaultValue,
  onChange,
  isDisabled,
  name_label,
  name_prefix,
  value_label,
  default_value,
  min_items,
  addLabel,
  chipOptions,
}: IInputVariableSelectorFieldProps) => {
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      disabled={isDisabled}
      render={({ field }) => {
        const handleOnChange = (value: IInputVariableValue[]) => {
          const seen = new Set<string>();
          const hasRepeatedNames = value
            .map((v) => v.label)
            .some((label) => {
              if (seen.has(label)) return true;
              seen.add(label);
              return false;
            });

          if (!hasRepeatedNames) {
            field.onChange(value);
            if (onChange) onChange(value);
            return true;
          }
          if (onChange) onChange(field.value);
          return false;
        };
        return (
          <InputVariableSelectorInput
            name={name}
            label={label}
            tooltip={tooltip}
            helperText={helperText}
            value={field.value}
            onChange={handleOnChange}
            name_label={name_label}
            name_prefix={name_prefix}
            value_label={value_label}
            default_value={default_value}
            isDisabled={isDisabled}
            min_items={min_items}
            addLabel={addLabel}
            chipOptions={chipOptions}
          />
        );
      }}
    />
  );
};
